import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ReservationRow } from '@smile/shared';
import { ArriveButton } from './ArriveButton';
import { DepartButton } from './DepartButton';
import { StatusButtons } from './StatusButtons';
import { CalledButton } from './CalledButton';
import {
  CZK,
  dayWord,
  LOCATION_LABEL,
  PAYMENT_LABEL,
  rowClass,
  spotLabel,
  STATUS_CLASS,
  STATUS_LABEL,
  TYPE_LABEL, TYPE_SHORT,
} from '../lib/format';
import { isEmail, isPhone, longDt } from '../lib/filters';

const money = (v: number) => `${CZK.format(v)} Kč`;

/**
 * The row that opens where it is.
 *
 * This file exists because the same panel now has to appear on three screens.
 * It was written for the dashboard, and the moment it went in the owner's
 * answer was that his dispatchers open a reservation from a list the same way
 * everywhere — so a copy of it on Rezervace that drifted by one field from the
 * copy on the dashboard would be worse than not having it. There is one copy,
 * and every screen renders it from the same `ReservationRow` the contract
 * already defines.
 */

/* ------------------------------------------------------------------ *
 * Shared row plumbing
 * ------------------------------------------------------------------ */

/**
 * Clicking a row opens the reservation *here*, in a panel that unfolds under
 * it, instead of navigating to the detail page.
 *
 * That is how the dispatchers already work: in the legacy extranet the Stav+ID
 * chip carries a caret and the row expands in place. Taking that away and
 * sending them to a separate page costs them the list they were reading — they
 * answer one question on the phone, press back, and have to find their place
 * in the arrivals again. The panel needs no request of its own: every field it
 * shows is already in the row the dashboard was handed, so it opens in the
 * time it takes to paint.
 *
 * The selection guard matters and survives: an operator selecting a licence
 * plate to paste it into a phone call must not have the row snap shut, or
 * open, mid-drag.
 */
export function rowToggle(openKey: string | null, setOpenKey: (k: string | null) => void) {
  return (r: ReservationRow, key: string) => {
    const isOpen = openKey === key;
    const toggle = () => setOpenKey(isOpen ? null : key);
    return {
      className: isOpen ? `${rowClass(r)} open` : rowClass(r),
      tabIndex: 0,
      role: 'button' as const,
      'aria-expanded': isOpen,
      'aria-label': `Rezervace ${r.id}, ${r.driverName}`,
      onClick: () => {
        if (window.getSelection()?.toString()) return;
        toggle();
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      },
    };
  };
}

/** What a row spreads onto its `<tr>`. */
export type RowProps = ReturnType<ReturnType<typeof rowToggle>>;

/* ------------------------------------------------------------------ *
 * The panel itself
 * ------------------------------------------------------------------ */

function Pair({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="dfield">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export const nic = <span className="empty">—</span>;
const dash = (v: string | number | null | undefined) =>
  v === null || v === undefined || v === '' ? nic : v;

/**
 * Read-only, three columns, in the order the legacy expanded row uses and the
 * detail page repeats: when, what car and where it stands, what they owe and
 * who they are. Nothing here is fetched — `r` is the row the list already has,
 * and the detail page renders from exactly the same shape, so the panel cannot
 * drift from it.
 *
 * Doplňkové služby are deliberately absent *here*, even though the Služby card
 * below now reads them. That card asks a different question — "what work is
 * outstanding in the yard" — and answers it one job at a time; it does not know
 * the full service list for any one booking, only the unfinished ones. Printing
 * a "Doplňkové služby" heading in this panel from that data would show a car
 * with a finished wash and an unfinished valet as having ordered one service.
 * The complete list belongs to the reservation, is not on the row, and arrives
 * with the detail page.
 */
/**
 * `expand`: which strip the old board showed open under this row — the
 * arrival form on Příjezdy, the departure strip on Odjezdy. Elsewhere both
 * stay behind their buttons.
 */
export function Drawer({ r, onClose, expand }: { r: ReservationRow; onClose: () => void; expand?: 'arrive' | 'depart' }) {
  const tel = r.driverPhone ? r.driverPhone.replace(/[^\d+]/g, '') : '';
  return (
    <div className="drawer">
      <div className="dhead">
        <span className={STATUS_CLASS[r.status] ?? 'stav gray'}>
          {STATUS_LABEL[r.status] ?? r.status}
        </span>
        <b>Rezervace #{r.id}</b>
        <span className="dsub">
          {TYPE_SHORT[r.type] ?? r.type} · {r.days} {dayWord(r.days)} · vytvořeno{' '}
          {longDt(r.created)}
        </span>
        <button type="button" className="dclose" onClick={onClose} aria-label="Zavřít náhled">
          ✕
        </button>
      </div>

      <div className="dcols">
        <dl>
          <h5>Termín</h5>
          <Pair label="Příjezd — plán">{longDt(r.arrival)}</Pair>
          <Pair label="Příjezd — skutečný">
            {r.arrivalReal ? longDt(r.arrivalReal) : <span className="empty">nepřijel</span>}
          </Pair>
          <Pair label="Odjezd — plán">{longDt(r.departure)}</Pair>
          <Pair label="Odjezd — skutečný">
            {r.departureReal ? longDt(r.departureReal) : <span className="empty">neodjel</span>}
          </Pair>
          <Pair label="Číslo letu">{dash(r.flightNumber)}</Pair>
        </dl>

        <dl>
          <h5>Vůz a stání</h5>
          <Pair label="Auto">{r.car || <span className="doplnit">Doplnit auto!</span>}</Pair>
          <Pair label="SPZ">
            {r.spz ? (
              <span className="spz">{r.spz}</span>
            ) : (
              <span className="doplnit">Doplnit SPZ!</span>
            )}
          </Pair>
          <Pair label="Stání">
            {dash(spotLabel(r.parking))}
            {r.parkingLocation && (
              <span className="dloc">
                {' '}
                · {LOCATION_LABEL[r.parkingLocation] ?? r.parkingLocation}
              </span>
            )}
          </Pair>
          <Pair label="Doporučené místo">{dash(spotLabel(r.parkingTarget))}</Pair>
          {/* Two fields rather than one "Keycafe příjezd / odjezd". The
              joined label was the longest in the panel by a wide margin and
              it set the width every column had to have; split, the three
              columns survive a narrower card. It also reads better — "4471 /
              —" asks the dispatcher to remember which half is which. */}
          <Pair label="Keycafe příjezd">{dash(r.keycafeArrival)}</Pair>
          <Pair label="Keycafe odjezd">{dash(r.keycafeDeparture)}</Pair>
        </dl>

        <dl>
          <h5>Platba a klient</h5>
          <Pair label="Cena">
            <b>{money(r.price)}</b>
            {r.priceOriginal !== null && r.priceOriginal !== r.price && (
              <span className="was"> (při vytvoření vč. služeb {money(r.priceOriginal)})</span>
            )}
          </Pair>
          <Pair label="Zaplaceno / zbývá">
            {money(r.paid)} /{' '}
            {r.outstanding > 0 ? (
              <span className="badge-pen">{money(r.outstanding)}</span>
            ) : (
              <span className="paidchip">0 Kč</span>
            )}
          </Pair>
          <Pair label="Způsob platby">
            {r.payment ? PAYMENT_LABEL[r.payment] ?? r.payment : nic}
          </Pair>
          <Pair label="Telefon">
            {isPhone(r.driverPhone) ? <a href={`tel:${tel}`}>{r.driverPhone}</a> : dash(r.driverPhone)}
          </Pair>
          <Pair label="E-mail">
            {isEmail(r.driverEmail) ? (
              <a href={`mailto:${r.driverEmail}`}>{r.driverEmail}</a>
            ) : (
              dash(r.driverEmail)
            )}
          </Pair>
          <Pair label="Klub">
            {r.clubTier ? (
              <span className={`flag ${r.clubTier === 'platinum' ? 'plat' : 'gold'}`}>
                {r.clubTier.toUpperCase()}
              </span>
            ) : (
              <span className="empty">bez členství</span>
            )}
          </Pair>
        </dl>
      </div>

      {r.note && (
        <div className="dnote">
          <b>Poznámka:</b> <span className="note">{r.note}</span>
        </div>
      )}

      <div className="dacts">
        <Link className="btn primary" to={`/rezervace/${r.id}`}>
          Otevřít celou rezervaci ›
        </Link>
        {/* The driver app's two legs, where the old board had them: open the row, press the link
            (the manager, 30 October 2026 call — drivers pick a car from Europort or Z Tuchoměřic /
            Z POPu and go straight to its leg). The same links are on the booking page. */}
        {['new', 'pending', 'paid', 'verified'].includes(r.status) && (
          <>
            <a className="btn" href={`https://ridic.smileparking.cz/e2t/${r.id}`} target="_blank" rel="noopener">
              Převoz do Tuchoměřic ›
            </a>
            <a className="btn" href={`https://ridic.smileparking.cz/t2e/${r.id}`} target="_blank" rel="noopener">
              Převoz na Europort ›
            </a>
          </>
        )}
        {/* Straight to the edit form (car, SPZ, flight, note, invoicing…),
            which lives on the booking page; the old row had these fields in
            its own lower half. */}
        {r.status !== 'deleted' && (
          <Link className="btn" to={`/rezervace/${r.id}?upravit=1`}>
            Opravit údaje
          </Link>
        )}
        {isPhone(r.driverPhone) && (
          <a className="btn" href={`tel:${tel}`}>
            Zavolat
          </a>
        )}
        {isEmail(r.driverEmail) && (
          <a className="btn" href={`mailto:${r.driverEmail}`}>
            Napsat e-mail
          </a>
        )}
        <ArriveButton r={r} compact defaultOpen={expand === 'arrive'} />
        <DepartButton r={r} compact defaultOpen={expand === 'depart'} />
        <CalledButton r={r} compact />
        <StatusButtons r={r} compact />
      </div>
    </div>
  );
}
